import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import React, { useState } from "react";
import { useGetDocumentsByEntityQuery } from "../../../api/documentsApi";
import {
  formatFileSize,
  getFileTypeName,
  isImageFile,
  isPdfFile,
  isPreviewableFile,
  isVideoFile,
} from "../../../utils/fileUtils";
import { formatDistanceToNow } from "date-fns";

interface ClaimFilesDialogProps {
  open: boolean;
  onClose: () => void;
  claimId: number;
}

interface PreviewDoc {
  url: string;
  originalFileName: string;
  contentType: string;
}

const formatCategory = (category: string | null | undefined): string => {
  if (!category) return "Claim Document";
  if (category === "PoliceReport") return "Police Report";
  return "Claim Document";
};

const ClaimFilesDialog: React.FC<ClaimFilesDialogProps> = ({
  open,
  onClose,
  claimId,
}) => {
  const { data: documents, isLoading, error } = useGetDocumentsByEntityQuery(
    { entityType: "Claim", entityId: claimId },
    { skip: !open }
  );

  const [previewDoc, setPreviewDoc] = useState<PreviewDoc | null>(null);

  const getFileTypeColor = (contentType: string) => {
    if (isPdfFile(contentType)) return "error";
    if (isImageFile(contentType)) return "primary";
    if (contentType.includes("document") || contentType.includes("word")) return "info";
    if (contentType.includes("sheet") || contentType.includes("excel")) return "success";
    return "default";
  };

  const getFileTypeIcon = (contentType: string) => {
    if (isPdfFile(contentType)) return <PdfIcon />;
    if (isImageFile(contentType)) return <ImageIcon />;
    return <DocumentIcon />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="claim-files-dialog-title"
    >
      <DialogTitle id="claim-files-dialog-title">
        <Typography variant="h6">Claim Files</Typography>
      </DialogTitle>

      <DialogContent>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">Error loading files. Please try again.</Alert>
        )}

        {!isLoading && !error && (!documents || documents.length === 0) && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <FileIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Files Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No files have been uploaded for this claim yet.
            </Typography>
          </Box>
        )}

        {!isLoading && !error && documents && documents.length > 0 && (
          <>
            <TableContainer component={Paper} elevation={1}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 600 }}>File</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {getFileTypeIcon(doc.contentType)}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {doc.originalFileName}
                            </Typography>
                            {doc.description && (
                              <Typography variant="caption" color="text.secondary">
                                {doc.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getFileTypeName(doc.contentType)}
                          size="small"
                          color={getFileTypeColor(doc.contentType)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatCategory(doc.documentCategory)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatFileSize(doc.fileSizeBytes)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDistanceToNow(new Date(doc.uploadedAt), { addSuffix: true })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(doc.uploadedAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                          <Tooltip title={isPreviewableFile(doc.contentType) ? "Preview" : "View"}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                isPreviewableFile(doc.contentType)
                                  ? setPreviewDoc(doc)
                                  : window.open(doc.url, "_blank")
                              }
                              color="primary"
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => window.open(doc.url, "_blank")} color="primary">
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {documents.length} file{documents.length !== 1 ? "s" : ""} total
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Dialog
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        maxWidth="md"
        fullWidth
      >
        {previewDoc && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="subtitle1" noWrap sx={{ pr: 2 }}>
                {previewDoc.originalFileName}
              </Typography>
              <IconButton size="small" onClick={() => setPreviewDoc(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "grey.900",
                minHeight: 300,
                p: 2,
              }}
            >
              {isImageFile(previewDoc.contentType) && (
                <Box
                  component="img"
                  src={previewDoc.url}
                  alt={previewDoc.originalFileName}
                  sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
                />
              )}
              {isVideoFile(previewDoc.contentType) && (
                <Box
                  component="video"
                  src={previewDoc.url}
                  controls
                  autoPlay
                  sx={{ maxWidth: "100%", maxHeight: "70vh" }}
                />
              )}
              {isPdfFile(previewDoc.contentType) && (
                <Box
                  component="iframe"
                  src={previewDoc.url}
                  title={previewDoc.originalFileName}
                  sx={{ width: "100%", height: "70vh", border: "none", backgroundColor: "white" }}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => window.open(previewDoc.url, "_blank")} startIcon={<DownloadIcon />}>
                Download
              </Button>
              <Button onClick={() => setPreviewDoc(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Dialog>
  );
};

export default ClaimFilesDialog;
